/**
 * Device-local heavy preview gating (docx/xlsx). Not synced to user settings.
 */

export const HEAVY_PREVIEW_GATE_STORAGE_KEY = "helloai.heavyPreviewGate.v1"

/** Trailing debounce before persisting auto-gate after slow render signals. */
export const HEAVY_PREVIEW_AUTO_DEBOUNCE_MS = 1200

/** After the user turns off manual gating, block auto re-enable for this long. */
export const HEAVY_PREVIEW_MANUAL_DISABLE_GRACE_MS = 90_000

export const HEAVY_PREVIEW_GATE_CHANGED_EVENT = "heavyPreviewGateChanged"

export type HeavyPreviewGateState = {
  manualGated: boolean
  autoGated: boolean
  ignoreAutoUntil: number
}

const DEFAULT_STATE: HeavyPreviewGateState = {
  manualGated: false,
  autoGated: false,
  ignoreAutoUntil: 0,
}

let autoGateDebounceTimer: number | null = null

function readState(): HeavyPreviewGateState {
  if (typeof window === "undefined") {
    return { ...DEFAULT_STATE }
  }
  try {
    const raw = window.localStorage.getItem(HEAVY_PREVIEW_GATE_STORAGE_KEY)
    if (!raw) {
      return { ...DEFAULT_STATE }
    }
    const parsed = JSON.parse(raw) as Partial<HeavyPreviewGateState>
    return {
      manualGated: Boolean(parsed.manualGated),
      autoGated: Boolean(parsed.autoGated),
      ignoreAutoUntil: typeof parsed.ignoreAutoUntil === "number" ? parsed.ignoreAutoUntil : 0,
    }
  } catch {
    return { ...DEFAULT_STATE }
  }
}

function writeState(state: HeavyPreviewGateState): void {
  if (typeof window === "undefined") {
    return
  }
  window.localStorage.setItem(HEAVY_PREVIEW_GATE_STORAGE_KEY, JSON.stringify(state))
  window.dispatchEvent(new CustomEvent(HEAVY_PREVIEW_GATE_CHANGED_EVENT))
}

export function getHeavyPreviewGateState(): HeavyPreviewGateState {
  return readState()
}

/** True when heavy previews should show the gate (manual preference or auto-detected slow). */
export function getEffectiveHeavyPreviewGated(): boolean {
  const s = readState()
  return s.manualGated || s.autoGated
}

export function setManualHeavyPreviewGated(checked: boolean): void {
  const state = readState()
  if (checked) {
    writeState({ ...state, manualGated: true })
    return
  }
  writeState({
    ...state,
    manualGated: false,
    autoGated: false,
    ignoreAutoUntil: Date.now() + HEAVY_PREVIEW_MANUAL_DISABLE_GRACE_MS,
  })
}

/**
 * Schedules auto-gate after debounce. If the user recently disabled manual gating,
 * the grace window blocks applying auto until it expires.
 */
export function scheduleHeavyPreviewAutoGate(): void {
  if (typeof window === "undefined") {
    return
  }
  if (autoGateDebounceTimer !== null) {
    window.clearTimeout(autoGateDebounceTimer)
  }
  autoGateDebounceTimer = window.setTimeout(() => {
    autoGateDebounceTimer = null
    const state = readState()
    if (Date.now() < state.ignoreAutoUntil) {
      return
    }
    if (state.manualGated) {
      return
    }
    writeState({ ...state, autoGated: true })
  }, HEAVY_PREVIEW_AUTO_DEBOUNCE_MS)
}

/**
 * One-time migration from synced `gateHeavyPreviews` when local state has never been set.
 * Call after user settings load.
 */
export function seedHeavyPreviewGateFromLegacyServer(
  legacyGateHeavyPreviews: boolean | undefined
): void {
  if (typeof window === "undefined") {
    return
  }
  if (legacyGateHeavyPreviews !== true) {
    return
  }
  const existing = window.localStorage.getItem(HEAVY_PREVIEW_GATE_STORAGE_KEY)
  if (existing !== null && existing !== "") {
    return
  }
  writeState({
    manualGated: true,
    autoGated: false,
    ignoreAutoUntil: 0,
  })
}
